import {
  PlannerConfigEntity,
  PlannerConfigEntityWithOptionalFields,
} from '@/services/configurator/PlannerConfigTypes';
import { ConfiguratorApi } from '@/utils/ConfiguratorApi';
import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { PlannerPageContainer } from './PlannerPageContainer';
import { useUser } from '@/components/providers/user-provider';
import { formattedDate } from '@/utils/date';
import { DraftSelector } from './DraftSelector';
import { PARAMS } from '@/utils/params';
import { toast } from 'sonner';
import { hasPermission } from '@/utils/validation';
import { EDIT_PHRASE_PERMISSIONS } from '@/config/feature-permissions';
import { useEnv } from '@/components/providers/env-provider';

function useIdentifier(): Array<string | undefined> {
  let [searchParams] = useSearchParams();

  const identifier = searchParams.get(PARAMS.FROM_IDENTIFIER) as string;

  return [identifier];
}

function useDraftId(): Array<string> {
  let [searchParams] = useSearchParams();

  const draftId = searchParams.get(PARAMS.FROM_DRAFT_ID) as string;

  return [draftId];
}

function usePromptForDraft(): Array<boolean> {
  let [searchParams] = useSearchParams();

  const promptForDraft = searchParams.get(PARAMS.PROMPT_TO_SELECT_DRAFT);

  return [promptForDraft === 'true'];
}

export function DraftPlannerPage() {
  const user = useUser();
  const [fromIdentifierParam] = useIdentifier();
  const [fromDraftIdParam] = useDraftId();
  const [promptForDraft] = usePromptForDraft();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [planner, setPlanner] =
    useState<PlannerConfigEntityWithOptionalFields>();
  const [error, setError] = useState<string>('');
  const { role, editConfigEnabled } = useUser();
  const { env } = useEnv();

  const disabledPhraseFields = !hasPermission(
    EDIT_PHRASE_PERMISSIONS[env],
    role,
    editConfigEnabled,
  );
  useEffect(() => {
    setError('');
    if (fromDraftIdParam && fromIdentifierParam) {
      setError('Must create from published planner or draft, not both');
    }

    if (fromIdentifierParam) {
      ConfiguratorApi.getInstance()
        .getPublishedPlanner('cte', fromIdentifierParam)
        .then((it) => setPlanner(it))
        .catch(() => setError('Planner not found'));
      return;
    }
    if (fromDraftIdParam) {
      ConfiguratorApi.getInstance()
        .getDraftPlanner('cte', fromDraftIdParam)
        .then((it) => setPlanner(it))
        .catch(() => setError('Planner not found'));
      return;
    }
  }, [fromIdentifierParam, fromDraftIdParam, setError, setPlanner]);

  const onSubmit = async (data: PlannerConfigEntityWithOptionalFields) => {
    try {
      setIsSubmitting(true);
      if (fromDraftIdParam) {
        await ConfiguratorApi.getInstance()
          .updateDraft(data as PlannerConfigEntity, data.id)
          .then(() => {
            toast.success(`Updated draft [${data.id}]`);
          });
        navigate('/');
      }
      if (fromIdentifierParam) {
        await ConfiguratorApi.getInstance()
          .createDraft(data as PlannerConfigEntity)
          .then((response) => {
            toast.success(`Saved new draft [${response.stagingId}]`);
          });
        navigate('/');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (error) {
    return <div>{error}</div>;
  }

  if (planner) {
    const title = fromDraftIdParam
      ? `Edit your draft from ${formattedDate(planner.lastModified!)}`
      : `Create draft from ${fromIdentifierParam}`;
    return (
      <div>
        <PlannerPageContainer
          mode='edit'
          planner={planner}
          title={title}
          description="Make changes to draft here. Click save when you're done."
          onSubmit={onSubmit}
          disabledPhraseFields={disabledPhraseFields}
          disabledFormFields={false}
          isSubmitting={isSubmitting}
          renderAdditionalHeaderContent={() => (
            <DraftSelector
              identifier={planner.identifier}
              username={user.email}
              defaultOpen={promptForDraft}
            />
          )}
        />
      </div>
    );
  }

  return null;
}
